import { COINS_DISABLED } from '../config/coinsFlag';
import React, { useMemo } from "react";
import { getUserTimeZone } from "../utils/timeAgo";
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

/**
 * WalletHistoryPremium (Revolut / Apple Wallet style)
 * - Grouped by day
 * - Today / Yesterday labels
 * - Premium row layout with icon badge, title, subtitle, right amount
 * - RTL support (pass dir="rtl")
 * - Uses user's phone timezone for display (server stores UTC)
 *
 * Props:
 *  - items: Array<{
 *      id: string
 *      type?: "credit" | "debit" | "neutral"
 *      title: string
 *      subtitle?: string
 *      amount: number            // positive or negative
 *      currencyLabel?: string    // e.g. "coins" or "عملة"
 *      createdAt: string|Date    // ISO or Date (UTC from server)
 *      meta?: { icon?: string }  // optional emoji/icon
 *    }>
 *  - locale: "en" | "ar"
 *  - dir: "ltr" | "rtl"
 *  - onViewAll?: () => void
 *  - timezone?: string (IANA timezone, defaults to browser)
 */

function toDate(d) {
  return d instanceof Date ? d : new Date(d);
}

function startOfDayInTz(date, tz) {
  // Get the date string in the user's timezone
  const d = new Date(date);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz || getUserTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const dateStr = fmt.format(d);
  return new Date(dateStr + 'T00:00:00');
}

function isSameDayInTz(a, b, tz) {
  return startOfDayInTz(a, tz).getTime() === startOfDayInTz(b, tz).getTime();
}

function formatDayLabel(date, locale, tz) {
  const timezone = tz || getUserTimeZone();
  const now = new Date();
  const y = new Date();
  y.setDate(now.getDate() - 1);

  if (isSameDayInTz(date, now, timezone)) return i18n.t('whp.today');
  if (isSameDayInTz(date, y, timezone)) return i18n.t('whp.yesterday');

  // Example: 18 Feb 2026 / 18 فبراير 2026
  const fmt = new Intl.DateTimeFormat(locale === "ar" ? "ar" : "en", {
    timeZone: timezone,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return fmt.format(date);
}

function formatTime(date, locale, tz) {
  const fmt = new Intl.DateTimeFormat(locale === "ar" ? "ar" : "en", {
    timeZone: tz || getUserTimeZone(),
    hour: "2-digit",
    minute: "2-digit",
  });
  return fmt.format(date);
}

function classifyType(item) {
  // Prefer explicit type, else infer from amount
  if (item.type) return item.type;
  if (item.amount > 0) return "credit";
  if (item.amount < 0) return "debit";
  return "neutral";
}

function formatAmount(amount) {
  // Correct: +5 / -5 / 0
  if (amount > 0) return `+${amount}`;
  if (amount < 0) return `-${Math.abs(amount)}`;
  return "0";
}

function pickBadgeIcon(itemType, locale, metaIcon) {
  if (metaIcon) return metaIcon;

  // Premium simple icons (no external libs)
  if (itemType === "credit") return "↑";
  if (itemType === "debit") return "↓";
  return "•";
}

function makeLabelDictionary(locale) {
  const isAr = locale?.startsWith?.('ar');
  return {
    history: i18n.t('whp.history') || (isAr ? 'السجل' : 'History'),
    viewAll: i18n.t('whp.viewAll') || (isAr ? 'عرض الكل' : 'View All'),
    emptyTitle: i18n.t('whp.noTransactionsYet') || (isAr ? 'لا توجد معاملات بعد' : 'No transactions yet'),
    emptySub: i18n.t('whp.yourWalletActivityWill') || (isAr ? 'سيظهر نشاط محفظتك هنا عند بدء استخدام العملات' : 'Your wallet activity will appear here once you start using coins'),
  };
}

function groupByDay(items, tz) {
  const timezone = tz || getUserTimeZone();
  const sorted = [...items].sort(
    (a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime()
  );

  const groups = [];
  for (const it of sorted) {
    const d = startOfDayInTz(toDate(it.createdAt), timezone);
    const last = groups[groups.length - 1];
    if (!last || last.day.getTime() !== d.getTime()) {
      groups.push({ day: d, items: [it] });
    } else {
      last.items.push(it);
    }
  }
  return groups;
}

export default function WalletHistoryPremium({
  items = [],
  locale = "en",
  dir = "ltr",
  timezone,
  onViewAll,
  viewAllLabel,
  emptyLabel,
}) {
  const tz = timezone || getUserTimeZone();
  const defaultLabels = useMemo(() => makeLabelDictionary(locale), [locale]);
  
  // Allow external override of labels
  const t = useMemo(() => ({
    ...defaultLabels,
    viewAll: viewAllLabel || defaultLabels.viewAll,
    emptyTitle: emptyLabel || defaultLabels.emptyTitle,
  }), [defaultLabels, viewAllLabel, emptyLabel]);

  const groups = useMemo(() => groupByDay(items, tz), [items, tz]);

  const isRTL = dir === "rtl";

  if (COINS_DISABLED) return null;

  return (
    <section dir={dir} style={styles.section}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <span style={styles.headerTitle}>{t.history}</span>
        </div>

        {onViewAll ? (
          <button
            type="button"
            onClick={onViewAll}
            style={{
              ...styles.viewAllBtn,
              ...(isRTL ? styles.viewAllBtnRTL : null),
            }}
            data-testid="wallet-history-view-all"
          >
            {t.viewAll}
            <span style={styles.chev}>{isRTL ? "‹" : "›"}</span>
          </button>
        ) : (
          <div style={{ width: 1 }} />
        )}
      </div>

      {/* Card */}
      <div style={styles.card}>
        {items?.length ? (
          <div style={styles.listWrap}>
            {groups.map((g) => {
              const dayLabel = formatDayLabel(g.day, locale, tz);
              return (
                <div key={g.day.toISOString()} style={styles.groupWrap}>
                  <div style={styles.groupHeader}>{dayLabel}</div>

                  <div style={styles.groupCard}>
                    {g.items.map((it, idx) => {
                      const itemType = classifyType(it);
                      const amountText = formatAmount(it.amount);
                      const currencyLabel = it.currencyLabel ?? i18n.t('whp.coins');
                      const timeText = formatTime(toDate(it.createdAt), locale, tz);

                      const amountColor =
                        itemType === "credit"
                          ? styles.amountCredit.color
                          : itemType === "debit"
                          ? styles.amountDebit.color
                          : styles.amountNeutral.color;

                      const badgeIcon = pickBadgeIcon(itemType, locale, it?.meta?.icon);

                      const badgeBg =
                        itemType === "credit"
                          ? styles.badgeCredit.background
                          : itemType === "debit"
                          ? styles.badgeDebit.background
                          : styles.badgeNeutral.background;

                      const badgeFg =
                        itemType === "credit"
                          ? styles.badgeCredit.color
                          : itemType === "debit"
                          ? styles.badgeDebit.color
                          : styles.badgeNeutral.color;

                      return (
                        <div key={it.id} style={styles.row} data-testid={`history-item-${it.id}`}>
                          {/* Left badge */}
                          <div
                            style={{
                              ...styles.badge,
                              background: badgeBg,
                              color: badgeFg,
                            }}
                            aria-hidden
                          >
                            {badgeIcon}
                          </div>

                          {/* Middle text */}
                          <div style={styles.main}>
                            <div style={styles.titleLine}>
                              <div style={styles.title} title={it.title}>
                                {it.title}
                              </div>
                              <div style={styles.time}>{timeText}</div>
                            </div>

                            {!!it.subtitle && (
                              <div style={styles.subtitle} title={it.subtitle}>
                                {it.subtitle}
                              </div>
                            )}
                          </div>

                          {/* Right amount */}
                          <div style={styles.right}>
                            <div
                              style={{
                                ...styles.amount,
                                color: amountColor,
                              }}
                              aria-label={`${amountText} ${currencyLabel}`}
                            >
                              {amountText}
                            </div>
                            <div style={styles.currency}>{currencyLabel}</div>
                          </div>

                          {/* Divider */}
                          {idx !== g.items.length - 1 && (
                            <div style={styles.divider} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={styles.emptyState} data-testid="wallet-history-empty">
            <div style={styles.emptyIcon} aria-hidden>
              ⟡
            </div>
            <div style={styles.emptyTitle}>{t.emptyTitle}</div>
            <div style={styles.emptySub}>{t.emptySub}</div>
          </div>
        )}
      </div>
    </section>
  );
}

/* Premium styles (no external CSS needed) */
const styles = {
  section: {
    width: "100%",
    maxWidth: 920,
    margin: "0 auto",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "10px 0 10px",
    gap: 12,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: 0.2,
    color: "#0F172A", // slate-900
  },

  viewAllBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "0",
    background: "transparent",
    cursor: "pointer",
    color: "#166534", // green-800
    fontWeight: 700,
    fontSize: 14,
    padding: "6px 8px",
    borderRadius: 10,
  },
  viewAllBtnRTL: { flexDirection: "row-reverse" },
  chev: {
    fontSize: 18,
    lineHeight: "14px",
    opacity: 0.9,
  },

  card: {
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow:
      "0 10px 30px rgba(15, 23, 42, 0.07), 0 2px 8px rgba(15, 23, 42, 0.05)",
    borderRadius: 18,
    overflow: "hidden",
  },

  listWrap: { padding: 14 },

  groupWrap: { marginBottom: 14 },
  groupHeader: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.55)",
    padding: "6px 8px",
  },

  groupCard: {
    background: "rgba(248, 250, 252, 0.9)", // slate-50-ish
    border: "1px solid rgba(15, 23, 42, 0.06)",
    borderRadius: 16,
    overflow: "hidden",
  },

  row: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "44px 1fr auto",
    gap: 12,
    padding: "12px 12px",
    alignItems: "center",
  },

  badge: {
    width: 40,
    height: 40,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    fontSize: 16,
    boxShadow: "0 6px 18px rgba(2, 6, 23, 0.08)",
  },

  badgeCredit: { background: "rgba(22, 163, 74, 0.14)", color: "#166534" },
  badgeDebit: { background: "rgba(239, 68, 68, 0.14)", color: "#991B1B" },
  badgeNeutral: { background: "rgba(100, 116, 139, 0.14)", color: "#334155" },

  main: { minWidth: 0, overflow: 'hidden' },
  titleLine: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0F172A",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.3,
  },
  time: {
    fontSize: 11,
    fontWeight: 600,
    color: "rgba(15, 23, 42, 0.45)",
  },
  subtitle: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: 600,
    color: "rgba(15, 23, 42, 0.50)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  right: {
    textAlign: "end",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
    minWidth: 72,
    flexShrink: 0,
  },
  amount: {
    fontSize: 15,
    fontWeight: 900,
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },
  amountCredit: { color: "#195B2F" },
  amountDebit: { color: "#DC2626" },
  amountNeutral: { color: "#334155" },

  currency: {
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15, 23, 42, 0.45)",
  },

  divider: {
    position: "absolute",
    left: 60,
    right: 12,
    bottom: 0,
    height: 1,
    background: "rgba(15, 23, 42, 0.06)",
  },

  emptyState: {
    padding: "28px 16px",
    textAlign: "center",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    margin: "0 auto 10px",
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "rgba(15, 23, 42, 0.06)",
    color: "rgba(15, 23, 42, 0.55)",
    fontSize: 22,
    fontWeight: 900,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#0F172A",
    marginBottom: 6,
  },
  emptySub: {
    fontSize: 13,
    fontWeight: 600,
    color: "rgba(15, 23, 42, 0.6)",
    maxWidth: 360,
    margin: "0 auto",
    lineHeight: 1.4,
  },
};
